import React, { useState } from 'react';
import { Plus, Trash2, Briefcase } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';

/**
 * WorkHistoryItem - Work history management component
 * Allows adding/removing/editing up to 5 work history entries
 * 
 * @param {Array} value - Array of work history objects [{company, position, duration, description}]
 * @param {Function} onChange - Callback when work history changes
 * @param {number} max - Maximum entries allowed (default: 5)
 * @param {boolean} disabled - Disable all interactions
 */
export const WorkHistoryItem = ({ 
  value = [], 
  onChange, 
  max = 5,
  disabled = false
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  
  const handleAdd = () => {
    if (value.length >= max || disabled) return;
    
    const newEntry = {
      company: '',
      position: '',
      duration: '',
      description: ''
    };
    
    const newValue = [...value, newEntry];
    onChange?.(newValue);
    setEditingIndex(newValue.length - 1);
  };
  
  const handleRemove = (index) => {
    if (disabled) return;
    
    const newValue = value.filter((_, i) => i !== index);
    onChange?.(newValue);
    
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };
  
  const handleUpdate = (index, field, fieldValue) => {
    if (disabled) return;
    
    const newValue = [...value];
    newValue[index] = {
      ...newValue[index],
      [field]: fieldValue
    };
    onChange?.(newValue);
  };
  
  const isMaxReached = value.length >= max;
  
  return (
    <div className="space-y-4" data-testid="work-history-container">
      {/* Header with count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase size={18} className="text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">
            Work History ({value.length}/{max})
          </span>
        </div>
        
        {!isMaxReached && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAdd}
            disabled={disabled || isMaxReached}
            className="h-8"
            data-testid="work-history-add-button"
          >
            <Plus size={14} className="mr-1" />
            Add Entry
          </Button>
        )}
      </div>
      
      {/* Work history entries */}
      {value.length === 0 ? (
        <Card className="border-dashed" data-testid="work-history-empty-state">
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <Briefcase size={40} className="text-muted-foreground mb-3 opacity-50" />
            <p className="text-sm text-muted-foreground mb-4">
              No work history added yet. Add your previous work experience to help employers know your background.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleAdd}
              disabled={disabled}
              data-testid="work-history-add-first-button"
            >
              <Plus size={16} className="mr-2" />
              Add First Entry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {value.map((entry, index) => (
            <Card 
              key={index} 
              className="relative"
              data-testid={`work-history-item-${index}`}
            >
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor={`company-${index}`} className="text-xs">
                        Company Name *
                      </Label>
                      <Input
                        id={`company-${index}`}
                        value={entry.company}
                        onChange={(e) => handleUpdate(index, 'company', e.target.value)}
                        placeholder="e.g., Bohol Construction Inc."
                        disabled={disabled}
                        data-testid={`work-history-company-${index}`}
                      />
                    </div>
                    
                    <div className="space-y-1.5">
                      <Label htmlFor={`position-${index}`} className="text-xs">
                        Position *
                      </Label>
                      <Input
                        id={`position-${index}`}
                        value={entry.position}
                        onChange={(e) => handleUpdate(index, 'position', e.target.value)}
                        placeholder="e.g., Senior Carpenter"
                        disabled={disabled}
                        data-testid={`work-history-position-${index}`}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor={`duration-${index}`} className="text-xs">
                      Duration *
                    </Label>
                    <Input
                      id={`duration-${index}`}
                      value={entry.duration}
                      onChange={(e) => handleUpdate(index, 'duration', e.target.value)}
                      placeholder="e.g., 2018 - Present or Jan 2020 - Dec 2022"
                      disabled={disabled}
                      data-testid={`work-history-duration-${index}`}
                    />
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor={`description-${index}`} className="text-xs">
                      Description <span className="text-muted-foreground">(max 500 chars)</span>
                    </Label>
                    <Textarea
                      id={`description-${index}`}
                      value={entry.description}
                      onChange={(e) => handleUpdate(index, 'description', e.target.value.slice(0, 500))}
                      placeholder="Brief description of your role and achievements..."
                      rows={3}
                      disabled={disabled}
                      maxLength={500}
                      data-testid={`work-history-description-${index}`}
                    />
                    <div className="text-xs text-muted-foreground text-right">
                      {entry.description?.length || 0}/500
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(index)}
                      disabled={disabled}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8"
                      data-testid={`work-history-remove-${index}`}
                    >
                      <Trash2 size={14} className="mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Max reached info */}
      {isMaxReached && (
        <p className="text-xs text-muted-foreground" data-testid="work-history-max-info">
          Maximum of {max} work history entries reached.
        </p>
      )}
    </div>
  );
};

export default WorkHistoryItem;